import React, { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Button from "@react/shared/ui/Button"
import { fmt, dateFmt } from "../helpers/format"
import { releaseRetention, resetRetention } from "../api/retention"
import ActionButton from "./ActionButton"

const RetentionBanner = ({ retention: initial, project, policies }) => {
  const [retention, setRetention] = useState(initial)
  const canManage = !!policies?.can_release_retention

  if (!retention || !retention.enabled) return null
  if (retention.cumul?.cents === 0 && !retention.released) return null

  const handleRelease = () => {
    if (!confirm(I18n.t("projects.retention.confirm_release", { amount: retention.cumul?.formatted }))) return
    releaseRetention(project.id).then(response => {
      if (response?.errors) { flashAlert(response.errors.join(", ")); return }
      setRetention(response)
      flashNotice(I18n.t("projects.retention.released_success"))
    })
  }

  const handleReset = () => {
    if (!confirm(I18n.t("projects.retention.confirm_reset"))) return
    resetRetention(project.id).then(response => {
      if (response?.errors) { flashAlert(response.errors.join(", ")); return }
      setRetention(response)
    })
  }

  if (retention.released) {
    return (
      <div className="px-7 mt-3">
        <div className="bg-emerald-50/60 border border-emerald-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-4">
          <span className="w-8 h-8 grid place-items-center rounded-full bg-emerald-100 text-emerald-700">
            <FontAwesomeIcon icon="unlock" />
          </span>
          <div className="flex flex-col">
            <span className="text-[10.5px] uppercase tracking-wider font-medium text-emerald-700">
              {I18n.t("projects.retention.eyebrow_released")}
            </span>
            <span className="text-[13px] font-semibold text-slate-900">
              {fmt(retention.cumul)} · {dateFmt(retention.released_at)}
            </span>
          </div>
          {retention.release_notes && (
            <span className="text-[12px] text-slate-600 italic max-w-md truncate" title={retention.release_notes}>
              "{retention.release_notes}"
            </span>
          )}
          {canManage && (
            <div className="ml-auto">
              <Button variant="ghost" size="sm" onClick={handleReset}>
                <FontAwesomeIcon icon="rotate-left" /> {I18n.t("projects.retention.reset")}
              </Button>
            </div>
          )}
        </div>
      </div>
    )
  }

  const overdue = retention.overdue
  const tone    = overdue ? "rose" : "amber"
  return (
    <div className="px-7 mt-3">
      <div className={`bg-${tone}-50/60 border border-${tone}-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-4`}>
        <span className={`w-8 h-8 grid place-items-center rounded-full bg-${tone}-100 text-${tone}-700`}>
          <FontAwesomeIcon icon="lock" />
        </span>
        <div className="flex flex-col">
          <span className={`text-[10.5px] uppercase tracking-wider font-medium text-${tone}-700`}>
            {I18n.t("projects.retention.eyebrow_retained")}
          </span>
          <span className="text-[13px] font-semibold text-slate-900">
            {fmt(retention.cumul)} · {retention.percentage}%
          </span>
        </div>

        {retention.release_due_on && (
          <div className="flex flex-col">
            <span className={`text-[10.5px] uppercase tracking-wider font-medium text-${tone}-700/80`}>
              {I18n.t("projects.retention.release_due_on")}
            </span>
            <span className={`text-[13px] ${overdue ? "text-rose-700 font-semibold" : "text-slate-900"}`}>
              {dateFmt(retention.release_due_on)}
              {overdue && <> · {I18n.t("projects.retention.overdue_badge")}</>}
            </span>
          </div>
        )}

        {canManage && (
          <div className="ml-auto">
            <ActionButton icon="unlock" onClick={handleRelease}>
              {I18n.t("projects.retention.release_cta")}
            </ActionButton>
          </div>
        )}
      </div>
    </div>
  )
}

export default RetentionBanner
